import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NotificationQueryKeys } from '@enums/react-query-keys';
import { NotificationTab } from '@interfaces/models/notification';
import { Notification } from '@interfaces/models/notification';
import { getNotificationsByPaginationV2, readNotification as sendReadNotification } from '@api/notifications';
import useUser from '@hooks/user/use-user';
import { Mode } from '@context/chat.context';
import { useEffect, useMemo, useRef } from 'react';
import { ApiResponseMeta } from '@interfaces/api';
import { NOTIFICATION_TAB_UPDATES, NOTIFICATION_TAB_MESSAGES } from '@constants/notification';

export type ReadNotificationsPayload = {
  notification: Notification;
  selectedTab: NotificationTab;
};

type PageData = {
  data: Notification[];
  meta: ApiResponseMeta;
};

type PrevData = {
  pages: PageData[];
  pageParams: [];
};
const useNotificationFeedQueryV2 = (notificationTab: NotificationTab, mode?: Mode) => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const limit = 60;

  const selectFilteredData = (notificationKey: string) => (newlyFetchedData) => {
    const cachedNotifications = queryClient.getQueryData<{
      pages?: PageData[];
    }>([notificationKey, user?.id]);

    const cachedNotificationIds =
      cachedNotifications?.pages
        ?.flatMap((page: PageData) => page?.data)
        ?.map((notification: Notification) => notification?.id) ?? [];

    const filteredNewData = {
      pages: [
        {
          data:
            newlyFetchedData?.pages
              ?.flatMap((page: PageData) => page.data)
              ?.filter((notification: Notification) => !cachedNotificationIds.includes(notification?.id)) ?? [],
          meta: newlyFetchedData?.pages?.[0]?.meta ?? {},
        },
        ...(cachedNotifications?.pages ?? []),
      ],
      pageParams: [],
    };

    return filteredNewData;
  };

  const selectFilteredMessages = selectFilteredData(NotificationQueryKeys.NOTIFICATION_FEED_MESSAGES_V2);
  const selectFilteredUpdates = selectFilteredData(NotificationQueryKeys.NOTIFICATION_FEED_UPDATES_V2);

  const {
    data: updatesData,
    isFetching: areUpdatesLoading,
    isFetchingNextPage: isFetchingNextPageUpdates,
    fetchNextPage: fetchNextPageUpdates,
    hasNextPage: hasNextPageUpdates,
    isError: hasUpdateErrorOccurred,
    refetch: refetchUpdates,
  } = useInfiniteQuery({
    queryKey: [NotificationQueryKeys.NOTIFICATION_FEED_UPDATES_V2, user?.id],
    queryFn: ({ pageParam = 0 }) => getNotificationsByPaginationV2(pageParam, limit, NOTIFICATION_TAB_UPDATES),
    initialData: {
      pages: [{ data: [], meta: {} as ApiResponseMeta }],
      pageParams: [],
    },
    getNextPageParam: (lastPage) => {
      if (lastPage?.meta?.count < limit) {
        return undefined;
      }
      return lastPage?.data?.length > 0 ? lastPage?.meta?.count + lastPage?.meta?.offset : undefined;
    },
    select: selectFilteredUpdates,
    retry: false,
  });

  // TODO: When switching tabs, invalidate queries to fetch only the initial page to prevent re-fetching all paginated pages. https://github.com/TanStack/query/discussions/1670
  const prevTab = useRef<NotificationTab | null>(null);

  useEffect(() => {
    if (prevTab.current && notificationTab !== prevTab.current) {
      const queryKey =
        notificationTab === 'updates'
          ? NotificationQueryKeys.NOTIFICATION_FEED_UPDATES_V2
          : NotificationQueryKeys.NOTIFICATION_FEED_MESSAGES_V2;

      queryClient.invalidateQueries({
        queryKey: [queryKey, user?.id],
        exact: true,
        refetchPage: (_, index) => index === 0,
      });
    }

    prevTab.current = notificationTab;
  }, [notificationTab, queryClient, user?.id]);

  const {
    data: messagesData,
    isFetching: areMessagesLoading,
    isFetchingNextPage: isFetchingNextPageMessages,
    fetchNextPage: fetchNextPageMessages,
    hasNextPage: hasNextPageMessages,
    isError: hasMessageErrorOccurred,
    refetch: refetchMessages,
  } = useInfiniteQuery({
    queryKey: [NotificationQueryKeys.NOTIFICATION_FEED_MESSAGES_V2, user?.id],
    queryFn: ({ pageParam = 0 }) => getNotificationsByPaginationV2(pageParam, limit, NOTIFICATION_TAB_MESSAGES),
    enabled: notificationTab === NOTIFICATION_TAB_MESSAGES && mode === 'popup',
    initialData: {
      pages: [{ data: [], meta: {} as ApiResponseMeta }],
      pageParams: [],
    },
    getNextPageParam: (lastPage) => {
      if (lastPage?.meta?.count < limit) {
        return undefined;
      }
      return lastPage?.data?.length > 0 ? lastPage?.meta?.count + lastPage?.meta?.offset : undefined;
    },
    select: selectFilteredMessages,
    retry: false,
  });

  const { mutate: readNotification } = useMutation<void, void, ReadNotificationsPayload>({
    mutationFn: (variables) => sendReadNotification(variables.notification?.id),
    onMutate: ({ notification, selectedTab }) => {
      const queryKey =
        selectedTab === NOTIFICATION_TAB_UPDATES
          ? NotificationQueryKeys.NOTIFICATION_FEED_UPDATES_V2
          : NotificationQueryKeys.NOTIFICATION_FEED_MESSAGES_V2;

      queryClient.setQueryData([queryKey, user?.id], (prevData: PrevData) => {
        return prevData
          ? {
              ...prevData,
              pages: prevData.pages.map((page: PageData) => ({
                ...page,
                data: page.data.map((notificationItem: Notification) =>
                  notificationItem?.id === notification?.id
                    ? { ...notificationItem, status: 'read' }
                    : notificationItem,
                ),
              })),
            }
          : undefined;
      });
    },
  });

  const updates = useMemo(
    () => updatesData?.pages.reduce((result, currentPage) => result.concat(currentPage.data), []),
    [updatesData],
  );
  const messages = useMemo(
    () => messagesData?.pages.reduce((mergedData, currentPage) => mergedData.concat(currentPage.data), []),
    [messagesData],
  );

  const getState = (isLoading, hasError, data, refetch) => ({
    loading: isLoading,
    error: hasError && !isLoading && data?.length === 0,
    empty: !hasError && !isLoading && data?.length === 0,
    data,
    refetch,
  });

  const updatesState = getState(areUpdatesLoading, hasUpdateErrorOccurred, updates, refetchUpdates);
  const messagesState = getState(areMessagesLoading, hasMessageErrorOccurred, messages, refetchMessages);

  return {
    readNotification,
    isFetchingNextPageUpdates,
    hasNextPageUpdates,
    fetchNextPageUpdates,
    isFetchingNextPageMessages,
    fetchNextPageMessages,
    hasNextPageMessages,
    updatesState,
    messagesState,
  };
};

export default useNotificationFeedQueryV2;
